import { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQueryClient } from "@tanstack/react-query";
import { routes } from "@/app/navigation/routes";
import { useAppSelector } from "@/app/hooks";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { toastService } from "@/shared/toast";
import { tokenService } from "@/services/auth/token.service";
import { useAddress } from "@/shared/hooks/useAddress";
import { propertyService } from "../model/propertyServices";
import type { PropertyRealStateDetailInfo, UserType } from "../model/propertiesTypes";
import { formatCurrency } from "@/utils/format.utils";
import { getDeviceId } from "@/features/auth/utils/device.utils";

const normalizeDate = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  if (/^\d{4}-\d{2}-\d{2}$/.test(trimmed)) return trimmed;
  const d = new Date(trimmed);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString().slice(0, 10);
};

const normalizeDateTimeIso = (value: unknown): string | null => {
  if (typeof value !== "string") return null;
  const trimmed = value.trim();
  if (!trimmed) return null;
  const normalized = trimmed.endsWith(" UTC")
    ? `${trimmed.replace(" UTC", "").replace(" ", "T")}Z`
    : trimmed;
  const d = new Date(normalized);
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
};

const isRecord = (value: unknown): value is Record<string, unknown> =>
  typeof value === "object" && value !== null;

const isSuccessfulAddResponse = (res: unknown): boolean => {
  if (!isRecord(res)) return false;
  const success = res["success"];
  if (typeof success === "boolean") return success === true;
  const status = res["status"];
  if (typeof status === "string") return status === "success";
  return false;
};

const getAddResponseMessage = (res: unknown): string | null => {
  if (!isRecord(res)) return null;
  const message = res["message"];
  return typeof message === "string" ? message : null;
};

import { extractCreatedPropertyIdFromAddResponse } from "../model/guestSession";

export interface UseStep3ViewModelParams {
  addressData?: { addressString?: string };
  propertyDetails?: PropertyRealStateDetailInfo;
  formData?: {
    firstName?: string;
    lastName?: string;
    userType?: UserType;
    isMailingAddress?: boolean;
  };
  onNext?: (data: { userType: UserType }) => void;
  onBack?: () => void;
}

export interface UseStep3ViewModelResult {
  userType: UserType | null;
  setUserType: (t: UserType) => void;
  isMailingAddress: boolean;
  setIsMailingAddress: (next: boolean) => void;
  isSubmitting: boolean;
  addressString: string;
  savingsAmount: string;
  handleSubmit: (e: React.FormEvent<HTMLFormElement>) => Promise<void>;
  handleBackClick: () => void;
}

export function useStep3ViewModel({
  addressData,
  propertyDetails,
  formData,
  onNext,
  onBack,
}: UseStep3ViewModelParams): UseStep3ViewModelResult {
  const user = useAppSelector((s) => s.auth.user);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { setSelectedAddress } = useAddress();

  const [userType, setUserTypeState] = useState<UserType | null>(formData?.userType || null);
  const [isMailingAddress, setIsMailingAddressState] = useState<boolean>(
    Boolean(formData?.isMailingAddress)
  );
  const [isSubmitting, setIsSubmitting] = useState(false);

  const addressString = useMemo(
    () => addressData?.addressString || "694 Center St, Chicopee, MA",
    [addressData?.addressString]
  );

  const totalTaxBill = propertyDetails?.taxInfo?.taxAmount;
  const savingsAmount = useMemo(() => {
    return totalTaxBill ? Math.round(Number(totalTaxBill) * 0.08) : 0;
  }, [totalTaxBill]);

  const setUserType = (t: UserType) => setUserTypeState(t);
  const setIsMailingAddress = (next: boolean) => setIsMailingAddressState(next);

  const handleSubmit: UseStep3ViewModelResult["handleSubmit"] = async (e) => {
    e.preventDefault();
    if (isSubmitting || !userType) return;

    // Use persistence (same source as axios) — AuthContext.hasToken can lag same-tab writes
    // because the `storage` event does not fire in the originating window.
    if (!tokenService.hasToken()) {
      toastService.show("Please login to continue.", "error");
      return;
    }

    if (!propertyDetails) {
      toastService.show("Property details are missing. Please search the address again.", "error");
      return;
    }

    const firstName = formData?.firstName?.trim() || "";
    const lastName = formData?.lastName?.trim() || "";

    try {
      setIsSubmitting(true);

      const ownerName = `${firstName} ${lastName}`.trim();
      const nowIso = new Date().toISOString();

      const apiAddress = propertyDetails.propertyInfo?.address;
      const fullAddress = apiAddress?.label || addressData?.addressString || "";
      const lastSaleDate = normalizeDate(propertyDetails.lastSaleDate);
      const lastApiSync = normalizeDateTimeIso(propertyDetails.lastUpdateDate);

      const userId = user?.id || "";

      // Backend requires at least one of user_id / ipAddress / deviceId.
      // Always send a stable deviceId so the property add never fails the identity gate.
      const deviceId = getDeviceId();

      // Defensive: do not create an unowned property when the auth/Redux user is missing.
      if (!userId && !deviceId) {
        toastService.show("Session not ready. Please try again.", "error");
        return;
      }

      const res = await propertyService.add({
        user_id: String(userId),
        ipAddress: "",
        deviceId,
        street: apiAddress?.street || "",
        city: apiAddress?.city || "",
        state: apiAddress?.state || "",
        zip: apiAddress?.zip || "",
        unitNumber: String(apiAddress?.unit ?? ""),
        address: fullAddress,
        propertyId: String(propertyDetails.id ?? ""),
        createdAt: nowIso,
        isMailingAddress,

        taxInfo: {
          improvementValue: Number(propertyDetails.taxInfo?.assessedImprovementValue || 0),
          marketLandValue: Number(propertyDetails.taxInfo?.marketLandValue || 0),
          marketValue: Number(propertyDetails.taxInfo?.marketValue || 0),
          taxAmount: Number(propertyDetails.taxInfo?.taxAmount || 0),
          assessedValue: Number(propertyDetails.taxInfo?.assessedValue || 0),
          taxDelinquentYear:
            propertyDetails.taxInfo?.taxDelinquentYear != null
              ? Number(propertyDetails.taxInfo.taxDelinquentYear)
              : null,
          taxYear: Number(
            propertyDetails.taxInfo?.year || propertyDetails.taxInfo?.assessmentYear || 0
          ),
        },

        ownerName,
        propertyUserType: userType,
        apiPropertyId: String(propertyDetails.id ?? ""),
        latitude: Number(propertyDetails.propertyInfo?.latitude || 0),
        longitude: Number(propertyDetails.propertyInfo?.longitude || 0),
        county: apiAddress?.county || "",
        estimatedValue: Number(propertyDetails.estimatedValue || 0),
        propertyType: String(propertyDetails.propertyType || ""),
        yearBuilt: Number(propertyDetails.propertyInfo?.yearBuilt || 0),
        squareFeet: Number(
          propertyDetails.propertyInfo?.livingSquareFeet ||
            propertyDetails.propertyInfo?.buildingSquareFeet ||
            0
        ),
        bedrooms: Number(propertyDetails.propertyInfo?.bedrooms || 0),
        bathrooms: Number(propertyDetails.propertyInfo?.bathrooms || 0),
        lotSquareFeet: Number(propertyDetails.propertyInfo?.lotSquareFeet || 0),
        ownerInfo: propertyDetails.ownerInfo
          ? {
              owner1FullName: String(propertyDetails.ownerInfo.owner1FullName || ""),
              owner2FullName: String(propertyDetails.ownerInfo.owner2FullName || ""),
              ownerOccupied: Boolean(propertyDetails.ownerInfo.ownerOccupied),
              ownershipLength: Number(propertyDetails.ownerInfo.ownershipLength || 0),
            }
          : null,
        estimatedMortgageBalance: Number(propertyDetails.estimatedMortgageBalance || 0),
        estimatedEquity: Number(propertyDetails.estimatedEquity || 0),
        equityPercent: Number(propertyDetails.equityPercent || 0),
        lastSaleDate,
        lastSalePrice: Number(propertyDetails.lastSalePrice || 0),
        lastApiSync,
        apiAddress: {
          fullAddress,
          street: apiAddress?.street || "",
          city: apiAddress?.city || "",
          state: apiAddress?.state || "",
          zip: apiAddress?.zip || "",
          county: apiAddress?.county || "",
        },
        propertyRawType: String(propertyDetails.propertyType || ""),
      });

      if (!isSuccessfulAddResponse(res)) {
        throw new Error(getAddResponseMessage(res) || "Failed to add property");
      }

      const createdPropertyId = extractCreatedPropertyIdFromAddResponse(res);
      // if (createdPropertyId) {
      //   setSelectedAddress(createdPropertyId);
      //   const accountId = user?.id != null ? String(user.id) : undefined;
      //   if (accountId) {
      //     void queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.PROPERTIES, accountId] });
      //   }
      //   toastService.show("Property added successfully", "success");
      // } else {
      //   toastService.show(
      //     "Property added. Select your property from the menu before paying.",
      //     "warning"
      //   );
      // }

      // Persist userType in parent formData for later steps (if any)

      if (!createdPropertyId) {
        toastService.show("Property creation failed. Please try again.", "error");

        return;
      }

      setSelectedAddress(createdPropertyId);

      const accountId = user?.id != null ? String(user.id) : undefined;

      if (accountId) {
        void queryClient.invalidateQueries({
          queryKey: [QUERY_KEYS.PROPERTIES, accountId],
        });
      }

      toastService.show("Property added successfully", "success");

      onNext?.({ userType });

      setTimeout(() => {
        navigate(routes.protestPayment, { replace: true });
      }, 0);
    } catch (err: unknown) {
      console.error("Failed to add property:", err);
      toastService.show("Failed to save property. Please try again.", "error");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleBackClick = () => {
    if (onBack) onBack();
  };

  return {
    userType,
    setUserType,
    isMailingAddress,
    setIsMailingAddress,
    isSubmitting,
    addressString,
    savingsAmount: formatCurrency(savingsAmount as number),
    handleSubmit,
    handleBackClick,
  };
}

// const handleBackClick = () => {
//   if (onBack) onBack();
// };

// return {
//   userType,
//   setUserType,
//   isMailingAddress,
//   setIsMailingAddress,
//   isSubmitting,
//   addressString,
//   savingsAmount: formatCurrency(calculatedTotalSavings),
//   selectedCountNum,
//   totalSavingsFormatted: formatCurrency(calculatedTotalSavings),
//   handleSubmit,
//   handleBackClick,
// };
// }
